import { useSyncExternalStore } from "react";
import { http } from "@/lib/api";

// Central feature-control state. Fetched once on app start and cached; the whole
// app reads availability from here (never scatters feature checks / hardcodes).
// Safe defaults: unknown keys are treated as enabled+visible so a missing/late
// config never bricks the editor (experimental features that are explicitly
// disabled in the DB simply won't appear).
let _map = {};
let _loaded = false;
const _listeners = new Set();
const emit = () => _listeners.forEach((l) => l());
const subscribe = (l) => { _listeners.add(l); return () => _listeners.delete(l); };
const getSnapshot = () => _map;

export async function loadFeatures() {
  try {
    const { data } = await http.get("/features");
    _map = data || {};
    _loaded = true;
    emit();
  } catch {
    _loaded = true; // keep safe defaults
  }
  return _map;
}

// Re-sync when the tab regains focus and every 60s so admin access changes apply live.
if (typeof window !== "undefined") {
  window.addEventListener("focus", () => { if (_loaded) loadFeatures(); });
  setInterval(() => { if (_loaded && document.visibilityState === "visible") loadFeatures(); }, 60000);
}

export function featuresLoaded() { return _loaded; }

const _entry = (key) => _map[key];

// Usable = enabled AND not in maintenance. Unknown key → true (safe default).
export function isFeatureEnabled(key) {
  const e = _entry(key);
  if (!e) return true;
  if (e.maintenance) return false;
  return e.enabled !== false;
}

// Visible in the UI = enabled AND visible AND not in maintenance (unless the admin
// left a maintenance feature visible, handled by callers that want the message).
export function isFeatureVisible(key) {
  const e = _entry(key);
  if (!e) return true;
  if (e.maintenance) return false;
  return e.enabled !== false && e.visible !== false;
}

export function featureMessage(key) {
  const e = _entry(key);
  return e && e.maintenance ? (e.message || "This feature is temporarily unavailable.") : "";
}

export function useFeatures() {
  return useSyncExternalStore(subscribe, getSnapshot);
}
